import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { defer, of, retry, switchMap, throwError, timer } from 'rxjs';

import {
  isOffline,
  OFFLINE_CONFIRM_MS,
  whenBackOnline,
} from './network-status';

/**
 * Methods safe to fire again on their own. A read has no side effect on the
 * server, so replaying it can only produce the answer the caller is still
 * waiting for. Writes are deliberately excluded: a POST that died with
 * `status === 0` may well have reached the server (the response is what was
 * lost), and replaying it would duplicate the operation — that case belongs to
 * the user, through the UI, not to an interceptor.
 */
const REPLAYABLE_METHODS = new Set(['GET', 'HEAD']);

/**
 * Opt-out for reads that must fail fast instead of waiting for the network —
 * a poll that will run again by itself, a request whose answer is worthless a
 * minute later, or one whose caller has its own retry.
 *
 * ```ts
 * this.http.get(url, {
 *   context: new HttpContext().set(SKIP_OFFLINE_RETRY, true),
 * });
 * ```
 */
export const SKIP_OFFLINE_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * How many times a single request may be replayed. Each replay costs an
 * offline→online transition, so this is not a backoff counter: it is the bound
 * on a connection that keeps flapping, so a request cannot follow a bad link
 * forever.
 */
const MAX_REPLAYS = 3;

/** No response reached the browser at all — the only failure shape worth replaying. */
function isNoResponseFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

/**
 * HTTP interceptor that holds a failed read until the device is back online and
 * then replays it.
 *
 * Without it, a screen loaded during a connectivity drop stays empty for good:
 * stores call their `load*` once, from `ngOnInit`, and nothing re-triggers them
 * when the connection returns — the offline modal closes over a screen whose
 * data never arrived, and only a reload or a round trip through another route
 * fixes it.
 *
 * ## How a failure is classified
 *
 * The browser never says *why* a request died, so the only shape acted on is
 * `status === 0` (no response reached the app at all), and the decision waits
 * {@link OFFLINE_CONFIRM_MS} before reading `navigator.onLine` — the flag lags
 * the failure it explains. Confirmed offline, the request is held until the
 * network is back. Still "online", it gets one blind attempt: the flag may be
 * lying, or the drop may already be over.
 *
 * Because the replay happens *inside* the pending observable, the caller never
 * learns the request failed: the store stays in its `loading` state (skeletons
 * keep showing, no error alert fires) and receives the response whenever it
 * lands. A request whose subscriber is gone by then — a destroyed component, a
 * `switchMap` that moved on — is unsubscribed like any other, taking its
 * `online` listener with it.
 *
 * ## Placement in the chain
 *
 * This interceptor must sit **above `TokenInterceptor`**. Each attempt calls
 * `next(request)` again (hence the `defer`), which re-runs the interceptors
 * below it — and that is the point: an outage can easily outlive the access
 * token, so the replay has to go through the token layer again to be sent with
 * a valid one and to get the usual refresh-and-retry on a 401. Re-subscribing
 * below `TokenInterceptor` would replay the request with the token captured
 * before the outage.
 *
 * The Sentry reporter sits below and therefore sees every individual attempt.
 * It drops the ones it classifies as offline reads rather than reporting them
 * once per attempt — a screen full of replayed requests is not a screen full of
 * incidents.
 *
 * Opt out per request with {@link SKIP_OFFLINE_RETRY}.
 */
export const OfflineRetryInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): ReturnType<HttpHandlerFn> => {
  const isReplayable =
    REPLAYABLE_METHODS.has(request.method.toUpperCase()) &&
    !request.context.get(SKIP_OFFLINE_RETRY);

  if (!isReplayable) {
    return next(request);
  }

  // `defer` so that a retry re-enters the chain from here instead of merely
  // re-subscribing to the observable built on the first attempt — see
  // "Placement in the chain" above.
  return defer(() => next(request)).pipe(
    retry({
      count: MAX_REPLAYS,
      delay: (error, attempt) => {
        if (!isNoResponseFailure(error)) return throwError(() => error);

        return timer(OFFLINE_CONFIRM_MS).pipe(
          switchMap(() => {
            // Offline confirmed: hold the request until the network is back.
            // The original error is handed to `whenBackOnline` as its timeout
            // reason, so a caller that waited two minutes sees exactly the
            // error it would have seen immediately — every store's error
            // handling, and the Sentry reporter's classification, keep working
            // unchanged.
            if (isOffline()) return whenBackOnline(error);

            // The flag never flipped. It may be lying (a captive portal, a
            // dropped VPN and hotel Wi-Fi all report `true`), or the drop may
            // have been shorter than the confirmation window and already be
            // over. Either way there is nothing to wait for, so the request
            // gets one blind attempt — it costs a single extra GET on failures
            // that turn out to be something else entirely, such as a
            // CORS-masked gateway error, and recovers every brief flap.
            return attempt === 1 ? of(null) : throwError(() => error);
          })
        );
      },
    })
  );
};
